import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useRounds } from '../hooks/useRounds';
import { useTeams } from '../hooks/useTeams';
import MatchCard from './MatchCard';
import './BracketTab.css';

const ROUND_ORDER   = ['r32', 'r16', 'qf', 'sf', 'final', 'third'];
const ROUND_LABELS  = { r32: 'Round of 32', r16: 'Round of 16', qf: 'Quarterfinals', sf: 'Semifinals', final: 'Final', third: '3rd Place' };

/* ── Bracket slot (read-only visual) ─────────────────────── */
function BracketSlot({ match, homeTeam, awayTeam }) {
  const confirmed = match.bothTeamsConfirmed;
  const isFinal   = match.status === 'final';

  const homeName = confirmed ? (homeTeam?.name ?? 'TBD') : 'TBD';
  const awayName = confirmed ? (awayTeam?.name ?? 'TBD') : 'TBD';
  const homeFlagCode = confirmed ? (homeTeam?.flagCode ?? '') : '';
  const awayFlagCode = confirmed ? (awayTeam?.flagCode ?? '') : '';

  return (
    <div className={`bkt-slot${!confirmed ? ' bkt-slot--tbd' : ''}`}>
      <div className={`bkt-team${isFinal && match.winnerId === match.homeTeamId ? ' bkt-team--winner' : ''}`}>
        <span className="bkt-flag">{homeFlagCode ? <span className={`fi fi-${homeFlagCode}`} /> : null}</span>
        <span className="bkt-name">{homeName}</span>
        {isFinal && <span className="bkt-score">{match.homeScore}</span>}
      </div>
      <div className="bkt-divider" />
      <div className={`bkt-team${isFinal && match.winnerId === match.awayTeamId ? ' bkt-team--winner' : ''}`}>
        <span className="bkt-flag">{awayFlagCode ? <span className={`fi fi-${awayFlagCode}`} /> : null}</span>
        <span className="bkt-name">{awayName}</span>
        {isFinal && <span className="bkt-score">{match.awayScore}</span>}
      </div>
    </div>
  );
}

/* ── Pair grouping helper ─────────────────────────────────── */
function chunkPairs(arr) {
  const pairs = [];
  for (let i = 0; i < arr.length; i += 2) pairs.push(arr.slice(i, i + 2));
  return pairs;
}

/* ── BracketTab ───────────────────────────────────────────── */
export default function BracketTab({ leagueId, uid }) {
  const rounds = useRounds();
  const teams  = useTeams();

  const [allMatches, setAllMatches] = useState([]);
  const [userPicks, setUserPicks]   = useState({});

  // All matches — needed for GF/GA and bracket
  useEffect(() => {
    return onSnapshot(collection(db, 'matches'), (snap) => {
      setAllMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('matches subscription error:', err));
  }, []);

  // User picks for this league
  useEffect(() => {
    if (!uid || !leagueId) return;
    const q = query(
      collection(db, 'picks'),
      where('leagueId', '==', leagueId),
      where('userId', '==', uid)
    );
    return onSnapshot(q, (snap) => {
      const data = {};
      snap.docs.forEach((d) => { data[d.data().matchId] = { id: d.id, ...d.data() }; });
      setUserPicks(data);
    }, (err) => console.error('picks subscription error:', err));
  }, [leagueId, uid]);

  // Knockout matches only, sorted by kickoff
  const knockoutMatches = useMemo(() => {
    return allMatches
      .filter((m) => m.round !== 'group')
      .sort((a, b) => (a.kickoffTime?.seconds ?? 0) - (b.kickoffTime?.seconds ?? 0));
  }, [allMatches]);

  // Group knockout matches by round
  const matchesByRound = useMemo(() => {
    const grouped = {};
    for (const m of knockoutMatches) {
      if (!grouped[m.round]) grouped[m.round] = [];
      grouped[m.round].push(m);
    }
    return grouped;
  }, [knockoutMatches]);

  // Tournament GF/GA from all final matches
  const tournamentStats = useMemo(() => {
    const stats = {};
    for (const m of allMatches) {
      if (m.status !== 'final' || m.homeScore == null) continue;
      if (!stats[m.homeTeamId]) stats[m.homeTeamId] = { gf: 0, ga: 0 };
      if (!stats[m.awayTeamId]) stats[m.awayTeamId] = { gf: 0, ga: 0 };
      stats[m.homeTeamId].gf += m.homeScore;
      stats[m.homeTeamId].ga += m.awayScore;
      stats[m.awayTeamId].gf += m.awayScore;
      stats[m.awayTeamId].ga += m.homeScore;
    }
    return stats;
  }, [allMatches]);

  if (!knockoutMatches.length) {
    return <div className="bkt-loading">Knockout bracket unlocks after the group stage.</div>;
  }

  // Rounds for the horizontal bracket tree (excludes 3rd place)
  const treeRounds  = ROUND_ORDER.filter((r) => r !== 'third' && matchesByRound[r]?.length);
  const thirdMatch  = matchesByRound['third']?.[0] ?? null;

  return (
    <div className="bkt">
      {/* ── Visual bracket tree ── */}
      <div className="bkt-tree-wrap">
        <div className="bkt-tree-inner">
        <div className="bkt-tree">
          {treeRounds.map((roundKey, colIdx) => {
            const isLastCol  = colIdx === treeRounds.length - 1;
            const roundMatches = matchesByRound[roundKey];
            // Final has only 1 match — no pairing needed
            const singleSlot = roundMatches.length === 1;
            return (
              <div key={roundKey} className="bkt-col">
                <div className="bkt-col-label">{ROUND_LABELS[roundKey]}</div>
                <div className="bkt-col-slots">
                  {singleSlot ? (
                    <div className="bkt-pair bkt-pair--single bkt-pair--no-connector">
                      <BracketSlot
                        match={roundMatches[0]}
                        homeTeam={teams[roundMatches[0].homeTeamId]}
                        awayTeam={teams[roundMatches[0].awayTeamId]}
                      />
                    </div>
                  ) : (
                    chunkPairs(roundMatches).map((pair, pairIdx) => (
                      <div
                        key={pairIdx}
                        className={`bkt-pair${isLastCol ? ' bkt-pair--no-connector' : ''}`}
                      >
                        {pair.map((match, slotIdx) => (
                          <div
                            key={match.id}
                            className={`bkt-slot-wrap bkt-slot-wrap--${slotIdx === 0 ? 'top' : 'bot'}`}
                          >
                            <BracketSlot
                              match={match}
                              homeTeam={teams[match.homeTeamId]}
                              awayTeam={teams[match.awayTeamId]}
                            />
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* ── 3rd Place match (inside wrap, below the tree) ── */}
        {thirdMatch && (
          <div className="bkt-third-wrap">
            <div className="bkt-third-label">3rd Place</div>
            <div className="bkt-third-slot">
              <BracketSlot
                match={thirdMatch}
                homeTeam={teams[thirdMatch.homeTeamId]}
                awayTeam={teams[thirdMatch.awayTeamId]}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Pick cards ── */}
      <div className="bkt-picks-section">
        <div className="bkt-picks-title">Make Your Picks</div>
        {knockoutMatches.map((match) => {
          const roundStatus = rounds[match.round]?.status ?? 'pending';
          return (
            <MatchCard
              key={match.id}
              match={match}
              homeTeam={teams[match.homeTeamId]}
              awayTeam={teams[match.awayTeamId]}
              userPick={userPicks[match.id] ?? null}
              roundStatus={roundStatus}
              leagueId={leagueId}
              uid={uid}
              isKnockout
              tournamentStats={tournamentStats}
            />
          );
        })}
      </div>
    </div>
  );
}
